/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import React, { FC, Fragment, useState } from "react";
import {
  MapData,
  MapEdgeType,
  MapNodeType,
  PlanTaskActualNodeInfoVehicle,
} from "client";
import {
  MarkerF,
  MarkerProps,
  OverlayViewF,
  PolylineF,
  PolylineProps,
} from "@react-google-maps/api";
import { Const } from "const";
import CloseIcon from "assets/icon_close.png";
import EdgeIcon from "assets/edge_icon.svg";
import { ReactComponent as HighlightIcon } from "assets/icon_highlight.svg";
import { Magnify, MagnifyProps } from "components/Map/Magnify";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import cssClass from "./NodeMapOverlay.module.css";
import { convertLatLngFromGeolocation } from "utils/Map";
import { MyMarkerClustererF } from "./MyMarkerClusterF";
import { ClusterIconStyle } from "@react-google-maps/marker-clusterer";
import LandmarkIcon from "assets/landmark.png";
import {
  removeDuplicateEdges,
  filterClosedEdges,
  filterOnewayEdges,
} from "../../utils/Map";
import { createOneWayIconDataUrl } from "./OnewayIcon";

interface MarkerPropsWithId extends MarkerProps {
  id?: string | number;
}

interface NodeType {
  type: "EdgeNode" | "NamedNode" | "LandmarkNode";
  label: string;
}

export const getNodeType = (description?: string): NodeType => {
  try {
    const escape = description
      ?.replaceAll(/(?<!\\)'/g, '"') // replace non escape single quat to double quat.
      .replaceAll(/\\'/g, "'"); // replace escape sequence of single quat
    const data = JSON.parse(escape ?? "");
    if ("for_ui_node" in data) {
      return {
        type: "LandmarkNode",
        label: String(data.for_ui_node),
      };
    }
    if ("name" in data) {
      return {
        type: "NamedNode",
        label: String(data.name),
      };
    }
  } catch (err: unknown) {
    // NOP
  }
  return { type: "EdgeNode", label: "" };
};

interface Props {
  mapData: MapData;
  onNodeSelect?: (node: MapNodeType | MapEdgeType) => void;
  selected?: MapNodeType | MapEdgeType;
  isMagnify?: boolean;
}

export const NormalEdgePolygon: FC<Props> = React.memo(
  ({ mapData, onNodeSelect }) => {
    const theme = useTheme();
    const lineOpts: google.maps.PolylineOptions = {
      strokeWeight: 5,
      strokeColor: theme.palette.grey[300],
      zIndex: Const.zIndex.edge,
    };
    const nodes = mapData?.mapInfo.flatMap((floor) => floor.nodes);
    const edges = mapData?.mapInfo.flatMap((floor) => floor.edges);
    const lines: (PolylineProps | null)[] = removeDuplicateEdges(edges)?.map(
      (edge) => {
        // find start position
        const start = convertLatLngFromGeolocation(
          nodes?.find((node) => node?.id === edge?.startNodeId)?.geolocation
        );
        // find end position
        const end = convertLatLngFromGeolocation(
          nodes?.find((node) => node?.id === edge?.endNodeId)?.geolocation
        );
        if (!start || !end) {
          // node not found.
          return null;
        }
        return {
          options: lineOpts,
          path: [start, end],
          onClick: () => {
            onNodeSelect?.(edge);
          },
        };
      }
    );
    return (
      <>
        {lines
          ?.filter((l) => l !== null)
          .map((line, idx) => {
            return <PolylineF {...line} key={`edge-${idx}`} />;
          })}
      </>
    );
  }
);

interface HighlightProps {
  highlightEdges: Array<PlanTaskActualNodeInfoVehicle>;
  currentNodeId?: number;
  mapData: MapData;
}

export const HighlightEdgePolygon: FC<HighlightProps> = ({
  highlightEdges,
  currentNodeId,
  mapData,
}) => {
  const theme = useTheme();

  const pastLineOpts: google.maps.PolylineOptions = {
    strokeWeight: 15,
    strokeColor: theme.palette.info.main,
    strokeOpacity: 1,
    zIndex: Const.zIndex.highlight,
  };
  const futureLineOpts: google.maps.PolylineOptions = {
    ...pastLineOpts,
    strokeColor: theme.palette.success.main,
  };
  const nodes = mapData.mapInfo.flatMap((floor) => floor.nodes);

  const points = highlightEdges
    .map((edge) => {
      const latlng = convertLatLngFromGeolocation(
        nodes.find((node) => node?.id === edge.nodeId)?.geolocation
      );
      return latlng
        ? {
            nodeId: edge.nodeId,
            latlng: latlng,
            arrived: edge.actualArrivalDatetime,
          }
        : undefined;
    })
    .filter((elm): elm is Exclude<typeof elm, undefined> => elm !== undefined);
  const currentIndex = points.findIndex((point) => !point.arrived);
  const pastPoints = points.slice(
    0,
    currentIndex !== -1 ? currentIndex : undefined
  );
  const futurePoints = currentIndex !== -1 ? points.slice(currentIndex) : [];
  const currentLine = points.slice(currentIndex - 1, currentIndex + 1);
  const startNodeIds = highlightEdges
    .filter((edge) => edge.nodeCategory === 1 || edge.actualArrivalDatetime)
    .filter(
      (edge) =>
        currentIndex === -1 || edge.nodeId !== points[currentIndex].nodeId
    )
    .map((edge) => edge.nodeId);
  const highlightNodes = points
    .map((point) => nodes.find((node) => node?.id === point.nodeId))
    .filter((elm): elm is Exclude<typeof elm, undefined> => elm !== undefined);
  return (
    <>
      {"future edge"}
      <PolylineF
        options={futureLineOpts}
        path={futurePoints.map((point) => point.latlng)}
      />
      {"past edge"}
      <PolylineF
        options={pastLineOpts}
        path={pastPoints.map((point) => point.latlng)}
      />
      {"current edge"}
      <PolylineF
        options={futureLineOpts}
        path={currentLine.map((point) => point.latlng)}
      />

      {highlightNodes.map((node, idx) => {
        const size =
          getNodeType(node.description).type !== "EdgeNode" ? 55 : 32;
        return (
          <OverlayViewF
            key={`highlightedge-${idx}`}
            position={convertLatLngFromGeolocation(node.geolocation)}
            mapPaneName="markerLayer"
            getPixelPositionOffset={(w, h) => ({
              x: -w / 2,
              y: -h / 2,
            })}
            zIndex={Const.zIndex.highlight}
          >
            <Box
              sx={{
                backgroundColor: startNodeIds.includes(node.id!)
                  ? theme.palette.info.main
                  : theme.palette.success.main,
                opacity: 0.5,
                width: size,
                height: size,
                borderRadius: "50%",
                zIndex: Const.zIndex.highlight,
              }}
            ></Box>
          </OverlayViewF>
        );
      })}
    </>
  );
};

interface NodeHighlightProps {
  mapData: MapData;
  nodeIds: number[];
}

export const HighlightNodePolygon: FC<NodeHighlightProps> = ({
  mapData,
  nodeIds,
}) => {
  const theme = useTheme();
  const nodes = mapData?.mapInfo.flatMap((floor) => floor.nodes);
  const highlightNodes: MapNodeType[] = [];
  for (const nodeId of nodeIds) {
    const node = nodes.find((value) => nodeId === value?.id);
    if (node) {
      highlightNodes.push(node);
    }
  }
  return (
    <>
      {highlightNodes.map((node, idx) => {
        const size =
          getNodeType(node.description).type !== "EdgeNode" ? 55 : 32;
        return (
          <OverlayViewF
            key={`highlightnode-${idx}`}
            position={convertLatLngFromGeolocation(node.geolocation)}
            mapPaneName="overlayMouseTarget"
            getPixelPositionOffset={(w, h) => ({
              x: -w / 2,
              y: -h / 2,
            })}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.info.main,
                opacity: 0.5,
                width: size,
                height: size,
                borderRadius: "50%",
              }}
            ></Box>
          </OverlayViewF>
        );
      })}
    </>
  );
};

export const EdgeNode: FC<Props> = React.memo(({ mapData, isMagnify }) => {
  const theme = useTheme();
  const edgeClusterIconStyles = new Array<ClusterIconStyle>(5).fill({
    height: 26,
    url: EdgeIcon,
    width: 26,
    textSize: 0,
    textColor: "transparent",
    className: "edgeNodeCls",
  });
  const edgeIconOpts: google.maps.Symbol = {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: theme.palette.grey[300],
    fillOpacity: 1,
    strokeColor: "transparent",
    strokeWeight: 1,
    scale: 13,
  };

  const nodes = mapData?.mapInfo.flatMap((floor) => floor.nodes);

  const markersProps: MarkerPropsWithId[] = nodes
    ?.filter((node) => getNodeType(node?.description).type === "EdgeNode")
    ?.filter((node) => node?.geolocation)
    ?.map((node) => {
      return {
        id: node?.id,
        position: convertLatLngFromGeolocation(node?.geolocation)!,
        icon: edgeIconOpts,
        zIndex: Const.zIndex.edge,
      };
    });
  return (
    <MyMarkerClustererF
      gridSize={26}
      averageCenter={false}
      zoomOnClick={false}
      styles={edgeClusterIconStyles}
      isMagnify={isMagnify}
    >
      {(clusterer) => (
        <Fragment>
          {markersProps.map(({ id, position, ...props }) => (
            <Fragment key={`edge-clusterer-fragment-${id}`}>
              {position && (
                <MarkerF
                  key={`edgenode-${id}`}
                  {...props}
                  position={position}
                  clusterer={clusterer}
                />
              )}
            </Fragment>
          ))}
        </Fragment>
      )}
    </MyMarkerClustererF>
  );
});

export const NamedNode: FC<Props> = React.memo(({ mapData, onNodeSelect }) => {
  const theme = useTheme();
  const namedNodeIconOpts: google.maps.Symbol = {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: theme.palette.grey[400],
    fillOpacity: 1,
    strokeColor: "black",
    strokeWeight: 1,
    labelOrigin: new google.maps.Point(0, 1.2),
    scale: 26,
  };

  const nodes = mapData?.mapInfo.flatMap((floor) => floor.nodes);
  const markersProps: MarkerPropsWithId[] = nodes
    ?.filter((node) => getNodeType(node?.description).type === "NamedNode")
    ?.filter((node) => node?.geolocation)
    ?.map((node) => {
      const nodeType = getNodeType(node?.description);
      return {
        id: node?.id,
        position: convertLatLngFromGeolocation(node?.geolocation)!,
        icon: namedNodeIconOpts,
        label: nodeType.label?.length
          ? {
              color: "white",
              className: cssClass.markerLabelOutline,
              text: nodeType.label,
            }
          : undefined,
        zIndex: Const.zIndex.namedNode,
        onClick: () => {
          onNodeSelect?.(node!);
        },
      };
    });
  return (
    <>
      {markersProps?.map(({ id, position, ...props }) => (
        <Fragment key={`namednode-fragment-${id}`}>
          {position && (
            <MarkerF {...props} key={`namednode-${id}`} position={position} />
          )}
        </Fragment>
      ))}
    </>
  );
});

export const RoadClosureNode: FC<Props> = React.memo((props) => {
  const [magnify, setMagnify] = useState<MagnifyProps | null>(null);
  const { mapData, onNodeSelect, selected, isMagnify } = props;
  // Closed Marker
  const roadClosureIconOpts: google.maps.Icon = {
    url: CloseIcon,
    anchor: new google.maps.Point(36, 36),
  };

  const roadClosureClusterIconStyles = new Array<ClusterIconStyle>(5).fill({
    url: CloseIcon,
    width: 72,
    height: 72,
    textSize: 0,
    textColor: "transparent",
  });
  // Get nodes & edges
  const nodes = mapData?.mapInfo.flatMap((floor) => floor.nodes);
  const edges = mapData?.mapInfo.flatMap((floor) => floor.edges);

  // Create Closed Marker
  const markersProps: (MarkerPropsWithId | null)[] = filterClosedEdges(
    edges
  )?.map((edge) => {
    // find start position
    const start = convertLatLngFromGeolocation(
      nodes?.find((node) => node?.id === edge?.startNodeId)?.geolocation
    );
    // find end position
    const end = convertLatLngFromGeolocation(
      nodes?.find((node) => node?.id === edge?.endNodeId)?.geolocation
    );
    if (!start || !end) {
      // node not found.
      return null;
    }
    const position: google.maps.LatLngLiteral = {
      lat: (start.lat + end.lat) / 2,
      lng: (start.lng + end.lng) / 2,
    };

    return {
      id: edge?.id,
      position: position,
      icon: roadClosureIconOpts,
      zIndex: Const.zIndex.roadStopNode,
      onClick: () => {
        setMagnify(null);
        onNodeSelect?.(edge);
      },
    };
  });

  // create Oneway Marker
  const oneWayMarkersProps: (MarkerPropsWithId | null)[] = filterOnewayEdges(
    edges
  )?.map((edge) => {
    // find start position
    const start = convertLatLngFromGeolocation(
      nodes?.find((node) => node?.id === edge?.startNodeId)?.geolocation
    );
    // find end position
    const end = convertLatLngFromGeolocation(
      nodes?.find((node) => node?.id === edge?.endNodeId)?.geolocation
    );
    if (!start || !end) {
      // node not found.
      return null;
    }
    const position: google.maps.LatLngLiteral = {
      lat: (start.lat + end.lat) / 2,
      lng: (start.lng + end.lng) / 2,
    };

    const vectorEdge = { x: end.lat - start.lat, y: end.lng - start.lng };
    // Normalize vector
    const magnitude = Math.sqrt(
      vectorEdge.x * vectorEdge.x + vectorEdge.y * vectorEdge.y
    );
    const normalizedVectorEdge = {
      x: vectorEdge.x / magnitude,
      y: vectorEdge.y / magnitude,
    };
    // Calculate the angle using the arctangent
    const angle = Math.atan2(normalizedVectorEdge.y, normalizedVectorEdge.x);
    // Convert angle to degrees
    const angleDegrees = (angle * 180) / Math.PI;
    const angleArrow = angleDegrees;

    const oneWayIconOpts: google.maps.Icon = {
      url: createOneWayIconDataUrl(angleArrow),
      anchor: new google.maps.Point(36, 36),
    };

    return {
      id: edge?.id,
      position: position,
      icon: oneWayIconOpts,
      zIndex: Const.zIndex.roadStopNode,
      onClick: () => {
        setMagnify(null);
        onNodeSelect?.(edge);
      },
    };
  });

  return (
    <>
      <MyMarkerClustererF
        averageCenter={false}
        styles={roadClosureClusterIconStyles}
        onClick={(cluster) => {
          const map = cluster.getMap()!;
          if (map instanceof google.maps.Map) {
            setMagnify({
              center: cluster.getCenter()!,
              bounds: cluster.getBounds()!,
              heading: map.getHeading(),
              onClose: () => {
                setMagnify(null);
              },
            });
          }
        }}
        zoomOnClick={false}
        isMagnify={isMagnify}
      >
        {(clusterer) => (
          <Fragment>
            {markersProps.map((markerProps) => {
              if (!markerProps) {
                return null;
              }
              const isSelected = markerProps.id === selected?.id;
              return (
                <MarkerF
                  {...markerProps}
                  key={`roadclosure-${markerProps.id}`}
                  clusterer={clusterer}
                >
                  {isSelected && (
                    <OverlayViewF
                      position={markerProps.position}
                      getPixelPositionOffset={(w, h) => {
                        return { x: -(w / 2), y: -(h / 2) };
                      }}
                      mapPaneName="overlayMouseTarget"
                    >
                      <HighlightIcon />
                    </OverlayViewF>
                  )}
                </MarkerF>
              );
            })}
          </Fragment>
        )}
      </MyMarkerClustererF>
      <Fragment>
        {oneWayMarkersProps.map((oneWayMarkersProp) => {
          if (!oneWayMarkersProp) {
            return null;
          }
          return (
            <MarkerF
              {...oneWayMarkersProp}
              key={`roadclosure-${oneWayMarkersProp.id}`}
            ></MarkerF>
          );
        })}
      </Fragment>
      {magnify && (
        <Magnify {...magnify}>
          <NormalEdgePolygon {...props} isMagnify={true} />
          <EdgeNode {...props} isMagnify={true} />
          <RoadClosureNode {...props} isMagnify={true} />
          <NamedNode {...props} isMagnify={true} />
          <LandmarkNode {...props} isMagnify={true} />
        </Magnify>
      )}
    </>
  );
});

export const LandmarkNode: FC<Props> = ({ mapData }) => {
  const landmarkNodeIconOpts: google.maps.Icon = {
    url: LandmarkIcon,
    anchor: new google.maps.Point(27, 27),
    labelOrigin: new google.maps.Point(27, 60),
  };

  const nodes = mapData?.mapInfo.flatMap((floor) => floor.nodes);
  const markersProps: MarkerPropsWithId[] = nodes
    ?.filter((node) => getNodeType(node?.description).type === "LandmarkNode")
    ?.filter((node) => node?.geolocation)
    ?.map((node) => {
      const nodeType = getNodeType(node?.description);
      return {
        id: node?.id,
        position: convertLatLngFromGeolocation(node?.geolocation)!,
        icon: landmarkNodeIconOpts,
        label: nodeType.label?.length
          ? {
              color: "white",
              className: cssClass.markerLabelOutline,
              text: nodeType.label,
            }
          : undefined,
        zIndex: Const.zIndex.landmarkNode,
      };
    });
  return (
    <>
      {markersProps?.map(({ id, position, ...props }) => (
        <Fragment key={`landmarknode-fragment-${id}`}>
          {position && (
            <MarkerF
              {...props}
              key={`landmarknode-${id}`}
              position={position}
            />
          )}
        </Fragment>
      ))}
    </>
  );
};

export const NodeMapOverlay: FC<Props> = (props) => {
  return (
    <>
      <NormalEdgePolygon {...props} />
      <EdgeNode {...props} />
      <RoadClosureNode {...props} />
      <NamedNode {...props} />
      <LandmarkNode {...props} />
    </>
  );
};
